import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Check,
  Download,
  FileText,
  Lightbulb,
  Pause,
  Play,
  Settings2,
  Target,
  UserRound,
  Users,
} from 'lucide-react';
import './HomeFeatureDemos.css';

const features = [
  {
    title: 'Define your direction',
    description: 'Vision, mission, values and strategic objectives.',
    icon: Target,
    to: '/foundation',
    tone: 'blue',
  },
  {
    title: 'Understand your business',
    description: 'PESTEL, Five Forces, SWOT and competitor profiles.',
    icon: BarChart3,
    to: '/strategic-framework',
    tone: 'purple',
  },
  {
    title: 'Choose your priorities',
    description: 'Guided strategy development linked to your objectives.',
    icon: Lightbulb,
    to: '/strategy-formulation',
    tone: 'blue',
  },
  {
    title: 'Align your people',
    description: 'Visual org charts, teams, positions and ownership.',
    icon: Users,
    to: '/team-collaboration',
    tone: 'purple',
  },
  {
    title: 'Make progress happen',
    description: 'Connected projects, tasks, subtasks, owners and progress views.',
    icon: Settings2,
    to: '/execution-monitoring',
    tone: 'blue',
  },
  {
    title: 'Present your plan',
    description: 'Custom visual reports and PDF exports.',
    icon: FileText,
    to: '/performance-tracking',
    tone: 'purple',
  },
];

const direction = [
  { label: 'Vision', text: 'Make expert design accessible to growing businesses.' },
  { label: 'Mission', text: 'Help ambitious teams turn good ideas into lasting growth.' },
  { label: 'Objectives', text: 'Grow qualified pipeline by 25% over the next 90 days.' },
];
const swot = [
  {
    label: 'Strengths',
    letter: 'S',
    tone: 'green',
    text: 'Trusted relationships with existing clients.',
  },
  {
    label: 'Weaknesses',
    letter: 'W',
    tone: 'pink',
    text: 'Too much reliance on one source of leads.',
  },
  {
    label: 'Opportunities',
    letter: 'O',
    tone: 'blue',
    text: 'Turn happy clients into a referral network.',
  },
  {
    label: 'Threats',
    letter: 'T',
    tone: 'orange',
    text: 'More studios competing for the same clients.',
  },
];
const priorities = [
  { label: 'Set strategic themes', detail: 'Focus on sustainable growth.' },
  { label: 'Evaluate options', detail: 'Compare referrals, partnerships and new offers.' },
  { label: 'Confirm priorities', detail: 'Start with a client referral programme.' },
];
const teams = [
  { label: 'Product', owner: 'Alex', responsibility: 'Validate the new offer' },
  { label: 'Marketing', owner: 'Jamie', responsibility: 'Launch the referral programme' },
  { label: 'Operations', owner: 'Sam', responsibility: 'Improve project handovers' },
];
const tasks = ['Define the offer', 'Build marketing assets', 'Invite the first five clients'];
const reportSections = [
  { label: 'Vision', text: direction[0].text },
  { label: 'Priorities', text: 'Grow through client referrals and stronger partnerships.' },
  { label: 'Roadmap', text: 'Launch the referral programme with clear owners and next steps.' },
];
const initialState = { direction: 0, swot: 0, priority: 0, team: 0, tasks: [0], report: 0 };
type SelectionKey = Exclude<keyof typeof initialState, 'tasks'>;

export default function HomeFeatureDemos() {
  const [demo, setDemo] = useState(initialState);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [announcement, setAnnouncement] = useState('');
  const [downloading, setDownloading] = useState(false);
  const demosRef = useRef<HTMLDivElement>(null);
  const touring = playing && inView && pageVisible && !reducedMotion;
  const progress = Math.round((demo.tasks.length / tasks.length) * 100);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compact = window.matchMedia('(max-width: 700px)');
    const syncMotion = () => {
      setReducedMotion(motion.matches);
      if (motion.matches) setPlaying(false);
    };
    const pauseCompactTour = () => {
      if (compact.matches) setPlaying(false);
    };
    syncMotion();
    setPlaying(!motion.matches && !compact.matches);
    motion.addEventListener('change', syncMotion);
    compact.addEventListener('change', pauseCompactTour);
    return () => {
      motion.removeEventListener('change', syncMotion);
      compact.removeEventListener('change', pauseCompactTour);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.05,
    });
    if (demosRef.current) observer.observe(demosRef.current);
    const syncVisibility = () => setPageVisible(!document.hidden);
    syncVisibility();
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, []);

  useEffect(() => {
    if (!touring) return;
    const timers = new Set<number>();
    const schedule = (key: SelectionKey | 'tasks', count: number) => {
      const timer = window.setTimeout(
        () => {
          timers.delete(timer);
          const choice = Math.random();
          setDemo((current) => {
            if (key === 'tasks') {
              const task = Math.floor(choice * count);
              return {
                ...current,
                tasks: current.tasks.includes(task)
                  ? current.tasks.filter((index) => index !== task)
                  : [...current.tasks, task],
              };
            }
            // Pick a different option every time, including when wrapping around.
            const offset = 1 + Math.floor(choice * (count - 1));
            return { ...current, [key]: (current[key] + offset) % count };
          });
          schedule(key, count);
        },
        3200 + Math.round(Math.random() * 4200)
      );
      timers.add(timer);
    };

    schedule('direction', direction.length);
    schedule('swot', swot.length);
    schedule('priority', priorities.length);
    schedule('team', teams.length);
    schedule('tasks', tasks.length);
    schedule('report', reportSections.length);
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [touring]);

  const select = (key: SelectionKey, index: number) => {
    setPlaying(false);
    setDemo((current) => ({ ...current, [key]: index }));
    const messages = {
      direction: `${direction[index]?.label}: ${direction[index]?.text}`,
      swot: `${swot[index]?.label}: ${swot[index]?.text}`,
      priority: `${priorities[index]?.label}: ${priorities[index]?.detail}`,
      team: `${teams[index]?.owner}, ${teams[index]?.label} lead: ${teams[index]?.responsibility}`,
      report: `Report section selected: ${reportSections[index]?.label}.`,
    };
    setAnnouncement(messages[key]);
  };

  const toggleTask = (index: number) => {
    setPlaying(false);
    const next = demo.tasks.includes(index)
      ? demo.tasks.filter((task) => task !== index)
      : [...demo.tasks, index];
    setDemo((current) => ({ ...current, tasks: next }));
    setAnnouncement(
      `${next.length} of ${tasks.length} example tasks complete. Project progress is ${Math.round((next.length / tasks.length) * 100)} percent.`
    );
  };

  const downloadSample = async () => {
    setPlaying(false);
    setDownloading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF();
      pdf.setTextColor(20, 29, 57);
      pdf.setFontSize(24);
      pdf.text('Strategic Plan', 22, 28);
      pdf.setFontSize(10);
      pdf.setTextColor(98, 113, 143);
      pdf.text('StratPlan | Illustrative example', 22, 38);
      reportSections.forEach((section, index) => {
        const y = 62 + index * 44;
        pdf.setTextColor(32, 91, 255);
        pdf.setFontSize(14);
        pdf.text(section.label, 22, y);
        pdf.setTextColor(20, 29, 57);
        pdf.setFontSize(12);
        pdf.text(pdf.splitTextToSize(section.text, 166), 22, y + 10);
      });
      pdf.save('stratplan-example-plan.pdf');
      setAnnouncement('The example strategic plan PDF has been downloaded.');
    } catch {
      setAnnouncement('The example PDF could not be downloaded. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const previews = [
    <div className="feature-preview feature-direction" key="direction">
      <div className="feature-tabs" role="group" aria-label="Direction example">
        {direction.map((item, index) => (
          <button
            type="button"
            key={item.label}
            aria-pressed={demo.direction === index}
            onClick={() => select('direction', index)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="feature-direction-statement">
        <span className="feature-label">Our {direction[demo.direction].label.toLowerCase()}</span>
        <p className="feature-changing-copy" key={demo.direction}>
          {direction[demo.direction].text}
        </p>
      </div>
      <span className="feature-preview-hint">
        <Target size={12} aria-hidden="true" /> A clear starting point for every decision
      </span>
    </div>,
    <div className="feature-preview feature-assessment" key="assessment">
      <span className="feature-label">SWOT analysis</span>
      <div className="feature-swot-grid" role="group" aria-label="SWOT example">
        {swot.map((item, index) => (
          <button
            type="button"
            className={`feature-swot-${item.tone}`}
            key={item.label}
            aria-pressed={demo.swot === index}
            onClick={() => select('swot', index)}
          >
            <span aria-hidden="true">{item.letter}</span>
            {item.label}
          </button>
        ))}
      </div>
      <p className="feature-insight feature-changing-copy" key={demo.swot}>
        {swot[demo.swot].text}
      </p>
    </div>,
    <div className="feature-preview feature-priorities" key="priorities">
      <div className="feature-priority-list" role="group" aria-label="Priorities example">
        {priorities.map((item, index) => (
          <button
            type="button"
            key={item.label}
            aria-pressed={demo.priority === index}
            onClick={() => select('priority', index)}
          >
            <span
              className={`feature-step-dot ${index < demo.priority ? 'is-complete' : ''}`}
              aria-hidden="true"
            >
              {index < demo.priority ? <Check size={13} aria-hidden="true" /> : index + 1}
            </span>
            {item.label}
            <ArrowRight size={13} aria-hidden="true" />
          </button>
        ))}
      </div>
      <p className="feature-insight feature-changing-copy" key={demo.priority}>
        {priorities[demo.priority].detail}
      </p>
    </div>,
    <div className="feature-preview feature-people" key="people">
      <div className="feature-org-chart">
        <div className="feature-org-ceo">
          <UserRound size={16} aria-hidden="true" />
          <span>CEO</span>
        </div>
        <div className="feature-org-teams" role="group" aria-label="Team ownership example">
          {teams.map((team, index) => (
            <button
              type="button"
              key={team.label}
              aria-pressed={demo.team === index}
              onClick={() => select('team', index)}
            >
              <UserRound size={15} aria-hidden="true" />
              {team.label}
            </button>
          ))}
        </div>
      </div>
      <div className="feature-owner feature-changing-copy" key={demo.team}>
        <span className="feature-owner-avatar">{teams[demo.team].owner[0]}</span>
        <div>
          <strong>
            {teams[demo.team].owner} · {teams[demo.team].label} lead
          </strong>
          <span>{teams[demo.team].responsibility}</span>
        </div>
      </div>
    </div>,
    <div className="feature-preview feature-execution" key="execution">
      <div className="feature-project-heading">
        <span className="feature-label">Launch the referral programme</span>
        <strong>{progress}%</strong>
      </div>
      <div
        className="feature-progress-track"
        role="progressbar"
        aria-label="Example project progress"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="feature-task-list">
        {tasks.map((task, index) => (
          <label key={task} className={demo.tasks.includes(index) ? 'is-complete' : ''}>
            <input
              type="checkbox"
              checked={demo.tasks.includes(index)}
              onChange={() => toggleTask(index)}
            />
            <span>{task}</span>
          </label>
        ))}
      </div>
      <span className="feature-preview-hint">
        {demo.tasks.length} of {tasks.length} tasks complete · Jamie owns this project
      </span>
    </div>,
    <div className="feature-preview feature-report" key="report">
      <div className="feature-report-paper">
        <strong>Strategic Plan</strong>
        <span className="feature-report-caption">Example studio · 90-day plan</span>
        <div role="group" aria-label="Report sections example">
          {reportSections.map((section, index) => (
            <button
              type="button"
              key={section.label}
              aria-pressed={demo.report === index}
              onClick={() => select('report', index)}
            >
              <strong>{section.label}</strong>
              <span>{section.text}</span>
              <i aria-hidden="true" />
            </button>
          ))}
        </div>
      </div>
      <div className="feature-report-actions">
        <span>
          <FileText size={18} aria-hidden="true" />
          Ready to share
        </span>
        <button type="button" onClick={downloadSample} disabled={downloading}>
          <Download size={13} aria-hidden="true" />
          {downloading ? 'Preparing…' : 'Sample PDF'}
        </button>
      </div>
    </div>,
  ];

  return (
    <div
      className="home-feature-demos"
      ref={demosRef}
      data-playing={touring}
      onFocusCapture={(event) => {
        if (!(event.target as HTMLElement).closest('.feature-play-toggle')) setPlaying(false);
      }}
    >
      <div className="feature-demo-toolbar">
        <span>
          <span className="feature-demo-dot" />
          Interactive examples <span className="feature-demo-instruction">· Click to explore</span>
        </span>
        <button
          type="button"
          className="feature-play-toggle"
          aria-pressed={playing}
          disabled={reducedMotion}
          title={
            reducedMotion
              ? 'Automatic playback is off to respect your reduced-motion preference'
              : undefined
          }
          onClick={() => setPlaying((current) => !current)}
        >
          {playing ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}
          {playing ? 'Pause examples' : 'Play examples'}
        </button>
      </div>
      <div className="home-feature-grid">
        {features.map(({ title, description, icon: Icon, to, tone }, index) => (
          <article key={title} className={`home-feature home-feature-${tone}`} aria-label={title}>
            <div className="home-feature-copy">
              <span className="home-feature-icon">
                <Icon size={28} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <div>
                <h3>
                  <Link to={to} aria-label={`Explore ${title.toLowerCase()}`}>
                    {title}
                  </Link>
                </h3>
                <p>{description}</p>
              </div>
            </div>
            {previews[index]}
          </article>
        ))}
      </div>
      <span className="feature-sr-only" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
